/**
 * CardOverline — the single dashboard eyebrow / section-label recipe.
 *
 * Brand type-ramp `overline` (9px / 700 / 0.8px tracking) + uppercase + tertiary
 * text + a fixed `mb-2`. Replaces the ~4 competing eyebrow styles across the
 * dashboard cards (`text-caption … uppercase tracking-wider font-bold`, the
 * redundant `text-overline … text-[9px] font-bold` stack, ad-hoc `text-[10px]`)
 * and the `mb-1/1.5/2/3` margin drift — one recipe, one margin.
 *
 * - `children`  the label text (sentence case is fine — rendered uppercase).
 * - `adornment` optional element rendered *adjacent* to the label (e.g. a chip).
 * - `action`    optional right-aligned slot (e.g. a "View all" link or a toggle).
 */
import { type ReactNode } from 'react';
import { clsx } from 'clsx';

export interface CardOverlineProps {
  children: ReactNode;
  adornment?: ReactNode;
  action?: ReactNode;
  className?: string;
}

export function CardOverline({ children, adornment, action, className }: CardOverlineProps) {
  return (
    <div className={clsx('mb-2 flex items-center gap-2', className)}>
      <span className="text-overline uppercase text-txt-tertiary truncate min-w-0">{children}</span>
      {adornment}
      {action ? <div className="ml-auto flex-shrink-0">{action}</div> : null}
    </div>
  );
}
